import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { getStatus } from '../pages/utils_v2/downloadUtils';

export default function ResultCard({
    title,
    reason,
    explanation,
    contact,
    guidance,
    timelines,
    portal,
    website,
    examples,
    legislationCode,
    status,
    questionsAnswered,
    geographical
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation('translation');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  function stripHtml(htmlString) {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const firstList = doc.querySelector("ul");
    let removedContent = false;
    if (firstList) {
      removedContent = true;
      let current = firstList;
      while (current) {
        const next = current.nextSibling;
        current.remove();
        current = next;
      }
    }
    let text = doc.body.textContent?.trim() || "";
    if (removedContent && text) text += "]";
    return text;
  }

  // Section component to reduce repetition
  const Section = ({ label, content }) => (
    <Grid item xs={12} sm={6}>
      <Typography sx={{ fontWeight: 'bold', fontSize: isMobile ? 16 : 20, mb: 0.5 }}>
        {t(label)}
      </Typography>
      <Typography sx={{ fontSize: isMobile ? 14 : 16, wordWrap: 'break-word', lineHeight: 1.6 }}>
        {content ? parse(content) : t('Not available')}
      </Typography>
    </Grid>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        width: 'auto',
        mb: 4,
        borderRadius: 5,
        boxShadow: 1,
        '&:hover': { boxShadow: 'md', borderColor: 'neutral.outlinedHoverBorder' },
      }}
    >
      <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>

        {/* Title */}
        <Typography
          variant="text"
          sx={{
            fontWeight: 'bold',
            fontSize: isMobile ? 15 : 16,
            lineHeight: 1.4,
            display: 'block',
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {/* Status + View more row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <span style={{ fontWeight: 'bold', fontSize: isMobile ? 13 : 14, color: '#d3080c' }}>
            {t(getStatus(status))}
          </span>
          <Button
            onClick={() => setIsOpen(!isOpen)}
            variant="text"
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: isMobile ? 13 : 14,
              p: isMobile ? '4px 8px' : undefined,
              minHeight: '40px',
            }}
          >
            {isOpen ? t('View less') : t('View more')}
          </Button>
        </Stack>

      </CardContent>

      {isOpen && (
        <Card>
          <Box margin={isMobile ? 1.5 : 2}>
            <Grid container spacing={isMobile ? 1.5 : 2}>

              {/* Reasons — always full width */}
              <Grid item xs={12}>
                <Typography sx={{ fontWeight: 'bold', fontSize: isMobile ? 16 : 20, mb: 0.5 }}>
                  {t('Result(s)')}
                </Typography>
                <Typography sx={{ fontSize: isMobile ? 14 : 16, wordWrap: 'break-word', lineHeight: 1.6 }}>
                  {reason ? parse(reason) : t('Not available')}
                </Typography>
                {explanation && (
                  <Typography sx={{ fontSize: isMobile ? 14 : 16, wordWrap: 'break-word', lineHeight: 1.6 }}>
                    {parse(explanation)}
                  </Typography>
                )}
              </Grid>

              {/* Two-column sections — full width on mobile */}
              <Section label="Contact Information" content={contact} />
              <Section label="How to Apply or Report" content={portal} />
              <Section label="Application and Regulatory Guidance" content={guidance} />
              <Section label="Timelines" content={timelines} />
              <Section label="Application Examples" content={examples} />
              <Section label="Program Website" content={website} />
              <Section label="Geographical Legislation" content={geographical} />

            </Grid>
          </Box>

          {/* Questions answered */}
          <Box margin={isMobile ? 1.5 : 2}>
            <Typography sx={{ fontWeight: 'bold', fontSize: isMobile ? 16 : 20, mb: 1 }}>
              {t('Your Answers to the Guided Search Questions')}
            </Typography>
            <Card style={{ backgroundColor: '#eaebed', borderRadius: 10 }}>
              <Box margin={isMobile ? 2 : 4}>
                {!questionsAnswered || questionsAnswered.length === 0
                  ? <Typography style={{ color: 'black', fontSize: isMobile ? 15 : 17 }}>{t('No questions')}</Typography>
                  : questionsAnswered.map((question, index) => (
                    <Typography
                      key={index}
                      style={{
                        color: 'black',
                        fontSize: isMobile ? 14 : 17,
                        lineHeight: 1.6,
                        marginBottom: '6px',
                      }}
                    >
                      {index + 1}. {stripHtml(question)}
                    </Typography>
                  ))
                }
              </Box>
            </Card>
          </Box>
        </Card>
      )}
    </Card>
  );
}